/**
 * Pagarle a un proveedor.
 *
 * ES EL ESPEJO DE `DialogCobro`, CON UNA DIFERENCIA QUE SE VE AL ABRIRLO
 * ---------------------------------------------------------------------
 * Un proveedor puede deber en las dos monedas a la vez, y cada una se salda con
 * la suya: no hay moneda de anclaje que las unifique como en la cuenta de un
 * cliente. Por eso lo primero es contra qué moneda de deuda va el pago, y recién
 * después con qué se paga. Si debe en una sola, la pregunta no se hace.
 *
 * LA COTIZACIÓN SE ESCRIBE A MANO, Y ESO ES A PROPÓSITO
 * ----------------------------------------------------
 * Pagar dólares con pesos es lo más común del mostrador, y el número que importa
 * es el que pasa el proveedor, no el del sistema. Ese número es el que después
 * hace medible el arbitraje en `v_resultado_negocio`: si se autocompletara en
 * silencio con la cotización del día, el resultado financiero daría cero siempre
 * y nadie sabría cuánto se ganó comprando barato.
 *
 * LO QUE VA A CANCELAR
 * --------------------
 * Abajo se listan las deudas abiertas con su antigüedad. Es informativo: el FIFO
 * de la base cancela de la más vieja a la más nueva y acá no se tilda nada. Está
 * para que antes de confirmar se vea qué factura va a quedar saldada.
 */
import { useEffect, useMemo, useState } from "react";
import { ArrowRight, Check } from "lucide-react";

import {
  convertir,
  desvioCotizacion,
  formatearCotizacion,
  type Moneda,
  parsearImporte,
} from "@tora/shared";

import { aNumero, type Caja } from "@/lib/tipos";
import { Aviso } from "@/components/ui/aviso";
import { Button } from "@/components/ui/button";
import { Campo } from "@/components/catalogo/Campos";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Kpi } from "@/components/ui/kpi";
import { TituloSeccion } from "@/components/ui/titulo-seccion";
import { Importe } from "@/components/money/importe";
import { EditorPagos, nuevoPago } from "@/components/cobro/EditorPagos";
import type { CuentaFinanciera, PagoCobro } from "@/components/cobro/logica";

import { AvisoCajasEnNegativo } from "./AvisoCajasEnNegativo";
import type { DatosPago, PagoProveedor } from "./consultas";
import {
  monedasQueDebe,
  saldoEn,
  haceCuanto,
  nombreDeuda,
  type DeudaProveedor,
  type SaldoProveedor,
} from "./logica";

export interface DialogPagoProveedorProps {
  proveedor: SaldoProveedor | null;
  deudas: ReadonlyArray<DeudaProveedor>;
  cuentas: CuentaFinanciera[];
  /** La del sistema. Es el default del campo, no lo que se manda siempre. */
  cotizacion: number;
  /** Los saldos de las cajas, para avisar si el pago deja una en negativo. */
  cajas?: ReadonlyArray<Caja>;
  guardando?: boolean;
  onCerrar: () => void;
  onConfirmar: (datos: DatosPago) => void;
}

export function DialogPagoProveedor({
  proveedor,
  deudas,
  cuentas,
  cotizacion,
  cajas = [],
  guardando = false,
  onCerrar,
  onConfirmar,
}: DialogPagoProveedorProps): JSX.Element {
  const monedas = proveedor ? monedasQueDebe(proveedor) : [];
  const [monedaDeuda, setMonedaDeuda] = useState<Moneda>(monedas[0] ?? "USD");
  const [pagos, setPagos] = useState<PagoCobro[]>([]);
  // Arranca vacia: el dolar es el que toma el proveedor en este pago, y no hay
  // uno general que lo reemplace. Proponer el del sistema (el de venta) era
  // dejar pasar un pago medido con un dolar que nadie pacto.
  const [cotizPactada, setCotizPactada] = useState<string>("");

  const pendiente = proveedor ? saldoEn(proveedor, monedaDeuda) : 0;

  // Al abrir (o al cambiar de moneda) se propone una fila con lo que falta.
  useEffect(() => {
    if (!proveedor) {
      setPagos([]);
      return;
    }
    const inicial = nuevoPago(
      cuentas,
      [],
      saldoEn(proveedor, monedaDeuda),
      monedaDeuda,
      cotizacion,
    );
    setPagos(inicial ? [inicial] : []);
  }, [proveedor, monedaDeuda, cuentas, cotizacion]);

  // Si el proveedor cambia, la moneda arranca en la primera que deba.
  useEffect(() => {
    if (proveedor) setMonedaDeuda(monedasQueDebe(proveedor)[0] ?? "USD");
    setCotizPactada("");
  }, [proveedor]);

  // Este era el peor de los seis campos que parseaban a mano. `Number("1.590")`
  // da 1,59, no 1590: en JavaScript el punto es decimal, y en Argentina se
  // escribe como separador de miles. Un pago de $ 1.000.000 al dolar "1.590"
  // cancelaba US$ 628.930 de deuda en vez de US$ 629, y dejaba en Ganancias
  // cientos de millones de arbitraje que nunca existieron.
  //
  // `parsearImporte` es la funcion que el sistema ya tenia para esto, y que
  // `DialogTransferencia` si usaba (`money.ts:445`).
  const cotizNum = parsearImporte(cotizPactada) ?? NaN;
  const cotizValida = Number.isFinite(cotizNum) && cotizNum > 0;

  // La segunda red, encima del parseo. Pactar un dolar distinto al del sistema
  // es normal y es justamente de donde sale el arbitraje; pactarlo al triple o
  // a un tercio no es una negociacion, es un dedazo. Como esta pantalla ya
  // conoce la cotizacion del sistema, el aviso puede ser relativo —mucho mas
  // fino que la banda absoluta de `@tora/shared`, que solo mira el orden de
  // magnitud— y frena antes de que el pago se escriba.
  const desvio = cotizValida ? desvioCotizacion(cotizNum, cotizacion) : 1;
  const cotizAbsurda = desvio > 3;

  /** Si algún medio está en una moneda distinta de la deuda, hay conversión. */
  const hayConversion = pagos.some((p) => {
    const cuenta = cuentas.find((c) => c.id === p.cuenta_id);
    return cuenta !== undefined && cuenta.moneda !== monedaDeuda;
  });

  /** Lo que cada medio cancela, ya en la moneda de la deuda. */
  const cancelado = useMemo(() => {
    if (hayConversion && !cotizValida) return 0;
    return pagos.reduce((t, p) => {
      if (p.importe === null) return t;
      const cuenta = cuentas.find((c) => c.id === p.cuenta_id);
      if (!cuenta) return t;
      return t + convertir(p.importe, cuenta.moneda, monedaDeuda, cotizValida ? cotizNum : 1);
    }, 0);
  }, [pagos, cuentas, monedaDeuda, cotizNum, cotizValida, hayConversion]);

  const hayImporteRoto = pagos.some((p) => p.importe === null);
  const sinMedios = pagos.length === 0;
  // Sin conversion el dolar no juega; con conversion es obligatorio.
  const puedeConfirmar =
    !sinMedios &&
    !hayImporteRoto &&
    cancelado > 0 &&
    (!hayConversion || (cotizValida && !cotizAbsurda));

  const restante = Math.round((pendiente - cancelado) * 100) / 100;
  const abiertas = deudas.filter((d) => d.moneda === monedaDeuda);

  function confirmar(): void {
    const lista: PagoProveedor[] = pagos
      .filter((p): p is PagoCobro & { importe: number } => p.importe !== null)
      .map((p) => {
        const cuenta = cuentas.find((c) => c.id === p.cuenta_id);
        return {
          cuenta_id: p.cuenta_id,
          medio: p.medio,
          importe: p.importe,
          // Solo se manda cuando hace falta, para no mentirle a la base que hubo
          // una conversión donde no la hubo.
          ...(cuenta && cuenta.moneda !== monedaDeuda ? { moneda_deuda: monedaDeuda } : {}),
          ...(cotizValida ? { cotizacion: cotizNum } : {}),
        };
      });
    onConfirmar({ pagos: lista });
  }

  return (
    <Dialog open={proveedor !== null} onOpenChange={(v) => !v && onCerrar()}>
      <DialogContent tamano="md" mobile="hoja">
        <DialogHeader>
          <DialogTitle>Pagarle a {proveedor?.nombre ?? ""}</DialogTitle>
          <DialogDescription>
            Cancela de la factura más vieja a la más nueva, dentro de la moneda que elijas.
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="space-y-4">
          {/* Contra qué moneda va. Solo aparece si debe en las dos: preguntar
              cuando no hay nada que elegir es un paso de más. */}
          {monedas.length > 1 ? (
            <div className="space-y-2">
              <TituloSeccion>Contra qué deuda</TituloSeccion>
              <div className="flex gap-2">
                {monedas.map((m) => (
                  <Button
                    key={m}
                    type="button"
                    variant={m === monedaDeuda ? "default" : "outline"}
                    size="sm"
                    onClick={() => setMonedaDeuda(m)}
                  >
                    {m === "USD" ? "Dólares" : "Pesos"}
                    <Importe monto={proveedor ? saldoEn(proveedor, m) : 0} moneda={m} tamano="sm" />
                  </Button>
                ))}
              </div>
            </div>
          ) : null}

          <Kpi etiqueta="Le debo" monto={pendiente} moneda={monedaDeuda} tamano="lg" />

          <div className="space-y-2">
            <TituloSeccion>Con qué se paga</TituloSeccion>
            <EditorPagos
              pagos={pagos}
              onChange={setPagos}
              cuentas={cuentas}
              objetivo={pendiente}
              monedaObjetivo={monedaDeuda}
              cotizacion={cotizValida ? cotizNum : cotizacion}
              etiquetaVacio="Agregar un medio de pago"
            />
            <AvisoCajasEnNegativo pagos={pagos} cajas={cajas} />
          </div>

          {/* La cotización solo importa cuando hay conversión de verdad. */}
          {hayConversion ? (
            <div className="space-y-2">
              <Campo
                label="Dólar de este pago"
                id="cotiz-pago-proveedor"
                ayuda="El que te pasó el proveedor. Es el que decide cuánto se ganó o se perdió contra el dólar del sistema."
              >
                <Input
                  id="cotiz-pago-proveedor"
                  type="text"
                  inputMode="decimal"
                  value={cotizPactada}
                  onChange={(e) => setCotizPactada(e.target.value)}
                  className="w-32 tabular-nums"
                />
              </Campo>
              {cotizPactada.trim() === "" ? (
                <Aviso>Cargá el dólar que te tomó el proveedor en este pago.</Aviso>
              ) : !cotizValida ? (
                <Aviso tono="destructive">Esa cotización no se entiende.</Aviso>
              ) : cotizAbsurda ? (
                <Aviso tono="destructive">
                  Ese dólar está {desvio >= 10 ? "muchísimo" : "muy"} lejos del del sistema, que hoy
                  es {formatearCotizacion(cotizacion)}. Si escribiste el punto como separador de
                  miles, sacalo: 1.590 se lee como 1,59.
                </Aviso>
              ) : null}
            </div>
          ) : null}

          {abiertas.length > 0 ? (
            <div className="space-y-2">
              <TituloSeccion>Qué va a cancelar</TituloSeccion>
              <Card padding="compacto" className="divide-y">
                {abiertas.map((d) => (
                  <div
                    key={d.movimiento_id}
                    className="flex items-center justify-between gap-3 py-1 text-sm"
                  >
                    <span className="min-w-0 truncate">
                      {nombreDeuda(d)}
                      <span className="ml-2 text-xs text-muted-foreground">
                        {haceCuanto(d.dias)}
                      </span>
                    </span>
                    <Importe monto={aNumero(d.pendiente)} moneda={d.moneda} tamano="sm" />
                  </div>
                ))}
              </Card>
            </div>
          ) : null}
        </DialogBody>

        {/* Cuánto queda después, fuera del scroll: es lo único que hace notar un
            cero de más con diez medios cargados. */}
        <div className="border-t px-6 py-3">
          <div className="flex items-center justify-between gap-3 text-sm">
            <span className="text-muted-foreground">Le quedo debiendo</span>
            <span className="flex items-center gap-2">
              <Importe monto={pendiente} moneda={monedaDeuda} tamano="sm" codigo={false} />
              <ArrowRight aria-hidden="true" className="size-3 text-muted-foreground" />
              <Importe monto={restante} moneda={monedaDeuda} />
            </span>
          </div>
          {restante < -0.009 ? (
            <Aviso tono="info" className="mt-2">
              Le vas a pagar más de lo que le debés. La diferencia queda a tu favor en la cuenta.
            </Aviso>
          ) : null}
        </div>

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onCerrar} disabled={guardando}>
            Cancelar
          </Button>
          <Button type="button" onClick={confirmar} disabled={!puedeConfirmar || guardando}>
            <Check aria-hidden="true" />
            Registrar el pago
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
